import React, { createContext, useContext, useState, useEffect, useRef } from 'react';
import api from '../services/api';
import toast from 'react-hot-toast';
import { buildAuthFailureResult } from '../utils/authFlow';

/* Exported so a caller that is not the app can supply the shape — the
 * marketing homepage mounts real product pages against a demo user. */
export const AuthContext = createContext({});

export const useAuth = () => useContext(AuthContext);

// One /auth/me per token at a time. React StrictMode runs the token effect
// twice in development, and every page load asked twice (staging UI run
// 2026-10-13); a second caller for the same token shares the first request.
let meInFlight = null; // { token, promise }
export function fetchMeOnce(token) {
  if (meInFlight && meInFlight.token === token) return meInFlight.promise;
  const promise = api.get('/auth/me').finally(() => {
    if (meInFlight && meInFlight.promise === promise) meInFlight = null;
  });
  meInFlight = { token, promise };
  return promise;
}

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [token, setToken] = useState(localStorage.getItem('token'));
  // The token whose user a login/signup response already gave us: no /auth/me for it.
  const userKnownForToken = useRef(null);

  useEffect(() => {
    if (token) {
      api.defaults.headers.common['Authorization'] = `Bearer ${token}`;
      if (userKnownForToken.current === token) {
        setLoading(false);
        return;
      }
      fetchUser();
    } else {
      setLoading(false);
    }
  }, [token]);

  const fetchUser = async () => {
    try {
      const response = await fetchMeOnce(localStorage.getItem('token') || token);
      const userData = response.data.data?.user || response.data.user || response.data;
      setUser(userData);
      setLoading(false);
    } catch (error) {
      // Only remove token if we got a 401, not for network errors
      if (error.response?.status === 401) {
        localStorage.removeItem('token');
        setToken(null);
      }
      // Always set loading to false even on network errors
      setLoading(false);
    }
  };

  const login = async (email, password) => {
    try {
      const response = await api.post('/auth/login', { email, password });
      const responseData = response.data.data?.data || response.data.data || response.data;
      const token = responseData.token;
      const user = responseData.user;

      if (!token) {
        throw new Error('No authentication token received');
      }

      localStorage.setItem('token', token);
      // No stored tenant key any more (every call uses the token); drop one an older build kept.
      localStorage.removeItem('tenantApiKey');
      api.defaults.headers.common['Authorization'] = `Bearer ${token}`;

      // The response carries the user: the token effect must not ask /auth/me again.
      if (user) userKnownForToken.current = token;
      setToken(token);
      setUser(user);

      // If user data is not in the response, fetch it
      if (!user) {
        await fetchUser();
      }

      toast.success('Welcome back!');
      return { success: true };
    } catch (error) {
      const result = buildAuthFailureResult(error, email);
      toast.error(result.error);
      return result;
    }
  };

  const loginWithGoogle = async (credential) => {
    try {
      const response = await api.post('/auth/google', { credential });
      const responseData = response.data.data?.data || response.data.data || response.data;
      const authToken = responseData.token;
      const user = responseData.user;

      if (!authToken) {
        throw new Error('No authentication token received');
      }

      localStorage.setItem('token', authToken);
      localStorage.removeItem('tenantApiKey');
      api.defaults.headers.common['Authorization'] = `Bearer ${authToken}`;

      if (user) userKnownForToken.current = authToken;
      setToken(authToken);
      setUser(user);

      if (!user) {
        await fetchUser();
      }

      toast.success('Signed in with Google');
      return { success: true };
    } catch (error) {
      const message = error.response?.data?.error || error.message || 'Google sign-in failed';
      toast.error(message);
      return { success: false, error: message };
    }
  };

  const register = async (userData) => {
    try {
      const response = await api.post('/auth/register', userData);
      const responseData = response.data.data || response.data;
      const message = responseData?.message || 'Account created. Check your email to verify your account.';

      localStorage.removeItem('token');
      localStorage.removeItem('tenantApiKey');
      delete api.defaults.headers.common['Authorization'];
      setToken(null);
      setUser(null);

      toast.success(message);
      return { success: true, message };
    } catch (error) {
      const message = error.response?.data?.error || 'Registration failed';
      toast.error(message);
      return { success: false, error: message };
    }
  };

  const logout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('tenantApiKey');
    delete api.defaults.headers.common['Authorization'];
    userKnownForToken.current = null;
    setToken(null);
    setUser(null);
    toast.success('Logged out successfully');
  };

  // A password change ends every session of the account and answers a fresh token for this one:
  // keep this tab signed in with it, and read the account again (name, email may have changed).
  const replaceSession = async (newToken) => {
    if (newToken) {
      localStorage.setItem('token', newToken);
      api.defaults.headers.common['Authorization'] = `Bearer ${newToken}`;
      setToken(newToken);
    }
    await fetchUser();
  };

  const value = {
    user,
    loading,
    login,
    loginWithGoogle,
    register,
    logout,
    replaceSession,
    isAuthenticated: !!user,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}
